import KYDCheckbox from "src/ui/KYDCheckbox";
import { SearchIcon } from "@chakra-ui/icons";
import KYDInput from "src/ui/KYDInput";
import KYDTimePicker from "src/ui/KYDTimePicker";
import { HStack, VStack, Image, Text } from "@chakra-ui/react";
import KYDHighlightTextArea from "src/ui/KYDHighlightTextArea";
import KYDSearchInput from "src/ui/KYDSearchInput";
import { KYDAdminEventSummary } from "@common/interfaces/KYDEvent";
import {
  InstantSearch,
  useInfiniteHits,
  useInstantSearch,
  useSearchBox,
} from "react-instantsearch";
import { SearchClient } from "algoliasearch";
import KYDImageUpload from "src/ui/KYDImageUpload";
import {
  KYDCampaignEvent,
  KYDEventBlockData,
} from "@common/interfaces/KYDCampaign";
import { Controller, useFormContext } from "react-hook-form";
import { nanoid } from "nanoid";
import { useEffect, useState } from "react";
import { KYDAdminFeed } from "@common/interfaces/KYDOrganization";
import { S3Response } from "react-s3-uploader";

const EventBlock = ({
  externalEvents,
  substitutionsItems,
  eventData,
  blockIndex,
  eventIndex,
  createUploadUrl,
  type,
  isDisabled,
}: {
  createUploadUrl: ({
    file,
    callback,
  }: {
    file: File;
    callback: (params: S3Response) => void | Promise<void | string>;
  }) => Promise<{ signedUrl: string; fileKey: string }>;
  externalEvents: KYDAdminFeed["external_events"];
  substitutionsItems: {
    key: string;
    label: string;
    value: string;
  }[];
  eventData: KYDEventBlockData | null;
  blockIndex: number;
  eventIndex: number;
  type: "event1col" | "event2col" | "eventList";
  isDisabled?: boolean;
}) => {
  const { items: events } = useInfiniteHits<KYDAdminEventSummary>({
    escapeHTML: false,
    showPrevious: false,
  });
  const { status: searchStatus } = useInstantSearch();
  const { refine } = useSearchBox();
  const [searchQuery, setSearchQuery] = useState("");
  const { control, setValue, watch, trigger } = useFormContext();
  const filteredExternalEvents = externalEvents
    .filter((event) =>
      event.title?.toLowerCase().includes(searchQuery?.toLowerCase())
    )
    .map((event) => ({
      ...event,
      name: event.title,
    }));
  const key = type === "event1col" ? "event" : `events.${eventIndex}`;

  const handleImageUpload = (result: S3Response & { cdn_url: string }) => {
    setValue(`blocks.${blockIndex}.${key}.custom_image`, result.cdn_url, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleSelect = (
    item: KYDAdminEventSummary | KYDAdminFeed["external_events"][number]
  ) => {
    const event = {
      id: item.id,
      name: "name" in item ? item.name : item.title,
      image: item.image,
      start_at: item.start_at,
      end_at: "end_at" in item ? item.end_at : "",
      link: item.link,
      venue: "venues" in item ? item.venue.name : item.venue_name,
      is_external: "name" in item ? false : true,
    } as KYDEventBlockData["event"];

    setValue(`blocks.${blockIndex}.${key}.event`, event);
    trigger(`blocks`);

    if ("name" in item) {
      setSearchQuery(item.name);
    } else {
      setSearchQuery(item.title);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    refine(query);
  };

  useEffect(() => {
    if (eventData?.event?.name) {
      setSearchQuery(eventData?.event?.name);
    }
  }, [eventData?.event?.name]);

  const image = eventData?.custom_image || eventData?.event?.image;

  return (
    <VStack gap={3} w="100%">
      <KYDSearchInput
        leftIcon={<SearchIcon color="#969696" fontSize="12px" mt={1} />}
        placeholder="Search for events"
        items={[...events, ...filteredExternalEvents]}
        maxHeight="300px"
        width="100%"
        searchField="name"
        value={searchQuery}
        onChange={handleSearch}
        loading={searchStatus === "loading"}
        onSelect={handleSelect}
        isDisabled={isDisabled}
        itemComponent={(item: KYDAdminEventSummary, index: number) => {
          return (
            <HStack
              key={`${item.id}-${index}`}
              py={2}
              px={3}
              borderBottom={
                index === events.length - 1 ? "none" : "1px solid #E2E8F0"
              }
              w="100%"
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <Image
                borderRadius="8px"
                width="70px"
                height="70px"
                objectFit="cover"
                src={item.image}
                alt="Event 2 Column"
              />
              <VStack
                w="100%"
                alignItems="flex-start"
                gap={1}
                justifyContent="flex-start"
              >
                <Text fontSize="14px" fontFamily="Helvetica">
                  {item.name}
                </Text>
                <Text fontSize="12px" color="#1A202CB2">
                  {item.display_start_at}
                </Text>
              </VStack>
            </HStack>
          );
        }}
      />

      {eventData?.event && (
        <>
          <KYDImageUpload
            image={image}
            onChange={handleImageUpload}
            createUploadUrl={createUploadUrl}
            isDisabled={!eventData?.event?.id || isDisabled}
          />
          <Controller
            name={`blocks.${blockIndex}.${key}.event.link`}
            control={control}
            disabled={isDisabled}
            render={({ field }) => (
              <KYDInput
                {...field}
                defaultValue={field.value}
                placeholder="Add link to redirect when event is clicked"
                label="Link"
              />
            )}
          />
          <Controller
            name={`blocks.${blockIndex}.${key}.button_text`}
            control={control}
            disabled={isDisabled}
            render={({ field }) => (
              <KYDInput
                {...field}
                defaultValue={field.value}
                placeholder="Optional"
                label="Button Text"
                w="100%"
              />
            )}
          />
          <KYDHighlightTextArea
            value={eventData?.body}
            onChange={(value) => {
              setValue(`blocks.${blockIndex}.${key}.body`, value, {
                shouldDirty: true,
                shouldTouch: true,
              });
            }}
            label="Event Body Copy"
            placeholder="Search for events"
            substitutions={substitutionsItems}
            isDisabled={isDisabled}
          />
          <HStack justifyContent="space-between" w="100%">
            <KYDTimePicker
              value={
                eventData?.door_time ? new Date(eventData?.door_time) : null
              }
              onChange={(value) => {
                setValue(`blocks.${blockIndex}.${key}.door_time`, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
              isDisabled={isDisabled}
              label="Doors"
            />
            <KYDTimePicker
              value={
                eventData?.show_time ? new Date(eventData?.show_time) : null
              }
              onChange={(value) => {
                setValue(`blocks.${blockIndex}.${key}.show_time`, value, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
              }}
              isDisabled={isDisabled}
              label="Show"
            />
          </HStack>
          <HStack gap={6} mt={3} w="100%" justifyContent="flex-start">
            <KYDCheckbox
              isChecked={!!eventData?.door_time}
              onChange={(e) => {
                setValue(
                  `blocks.${blockIndex}.${key}.door_time`,
                  e.target.checked ? new Date().toISOString() : null,
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                  }
                );
              }}
              label="Doors"
              isDisabled={isDisabled}
            />
            <KYDCheckbox
              isChecked={!!eventData?.show_time}
              onChange={(e) => {
                setValue(
                  `blocks.${blockIndex}.${key}.show_time`,
                  e.target.checked ? new Date().toISOString() : null,
                  {
                    shouldDirty: true,
                    shouldTouch: true,
                  }
                );
              }}
              label="Showtime"
              isDisabled={isDisabled}
            />
          </HStack>
        </>
      )}
    </VStack>
  );
};

export default EventBlock;
